import type { Placement } from "@floating-ui/react";
import {
	autoUpdate,
	FloatingFocusManager,
	FloatingPortal,
	flip,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useListNavigation,
	useRole,
	useTransitionStyles,
} from "@floating-ui/react";
import clsx from "clsx";
import {
	type ButtonHTMLAttributes,
	Children,
	cloneElement,
	createContext,
	type HTMLAttributes,
	isValidElement,
	type ReactNode,
	useContext,
	useRef,
	useState,
} from "react";

// ─── Types ───────────────────────────────────────────────────────────

interface DropdownMenuContextValue {
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	activeIndex: number | null;
	refs: ReturnType<typeof useFloating>["refs"];
	floatingStyles: ReturnType<typeof useFloating>["floatingStyles"];
	context: ReturnType<typeof useFloating>["context"];
	getReferenceProps: ReturnType<typeof useInteractions>["getReferenceProps"];
	getFloatingProps: ReturnType<typeof useInteractions>["getFloatingProps"];
	getItemProps: ReturnType<typeof useInteractions>["getItemProps"];
	listRef: React.MutableRefObject<(HTMLButtonElement | null)[]>;
	transitionStyles: React.CSSProperties;
	isMounted: boolean;
}

interface DropdownMenuProps {
	children: ReactNode;
	placement?: Placement;
	offsetValue?: number;
}

interface DropdownMenuTriggerProps
	extends ButtonHTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
}

interface DropdownMenuContentProps extends HTMLAttributes<HTMLDivElement> {
	children: ReactNode;
}

type ItemVariant = "default" | "danger";

interface DropdownMenuItemProps
	extends ButtonHTMLAttributes<HTMLButtonElement> {
	children: ReactNode;
	onSelect?: () => void;
	variant?: ItemVariant;
}

interface DropdownMenuSeparatorProps extends HTMLAttributes<HTMLDivElement> {}

// ─── Context ─────────────────────────────────────────────────────────

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(
	null,
);

const useDropdownMenuContext = () => {
	const context = useContext(DropdownMenuContext);
	if (!context) {
		throw new Error(
			"DropdownMenu compound components must be used within <DropdownMenu>",
		);
	}
	return context;
};

// ─── Root ────────────────────────────────────────────────────────────

const DropdownMenuRoot = ({
	children,
	placement = "bottom-start",
	offsetValue = 4,
}: DropdownMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState<number | null>(null);
	const listRef = useRef<(HTMLButtonElement | null)[]>([]);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement,
		transform: false,
		middleware: [offset(offsetValue), flip(), shift({ padding: 8 })],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context, { role: "menu" });
	const listNavigation = useListNavigation(context, {
		listRef,
		activeIndex,
		onNavigate: setActiveIndex,
		loop: true,
	});

	const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
		[click, dismiss, role, listNavigation],
	);

	const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
		duration: 150,
		initial: { opacity: 0, transform: "scale(0.95)" },
		open: { opacity: 1, transform: "scale(1)" },
		close: { opacity: 0, transform: "scale(0.95)" },
	});

	return (
		<DropdownMenuContext.Provider
			value={{
				isOpen,
				setIsOpen,
				activeIndex,
				refs,
				floatingStyles,
				context,
				getReferenceProps,
				getFloatingProps,
				getItemProps,
				listRef,
				transitionStyles,
				isMounted,
			}}
		>
			{children}
		</DropdownMenuContext.Provider>
	);
};

// ─── Trigger ─────────────────────────────────────────────────────────

const Trigger = ({
	children,
	className,
	...props
}: DropdownMenuTriggerProps) => {
	const { refs, getReferenceProps } = useDropdownMenuContext();

	return (
		<button
			ref={refs.setReference}
			className={clsx("inline-flex items-center", className)}
			{...getReferenceProps()}
			{...props}
		>
			{children}
		</button>
	);
};

// ─── Content ─────────────────────────────────────────────────────────

const Content = ({
	children,
	className,
	...props
}: DropdownMenuContentProps) => {
	const {
		refs,
		floatingStyles,
		context,
		getFloatingProps,
		transitionStyles,
		isMounted,
	} = useDropdownMenuContext();

	if (!isMounted) return null;

	let itemIndex = 0;
	const indexedChildren = Children.map(children, (child) => {
		if (isValidElement(child) && child.type === Item) {
			const currentIndex = itemIndex;
			itemIndex++;
			return cloneElement(child, { _index: currentIndex } as Record<
				string,
				unknown
			>);
		}
		return child;
	});

	return (
		<FloatingPortal>
			<FloatingFocusManager context={context} modal={false}>
				<div
					ref={refs.setFloating}
					style={{ ...floatingStyles, ...transitionStyles }}
					className={clsx(
						"z-50 min-w-[180px] rounded-8 border border-grayscale-200 bg-grayscale-white py-4 shadow-sm",
						className,
					)}
					{...getFloatingProps()}
					{...props}
				>
					{indexedChildren}
				</div>
			</FloatingFocusManager>
		</FloatingPortal>
	);
};

// ─── Item ────────────────────────────────────────────────────────────

const itemVariants: Record<ItemVariant, string> = {
	default: "text-grayscale-800 hover:bg-primary-100/60 focus:bg-primary-100/60",
	danger: "text-red-600 hover:bg-red-100/60 focus:bg-red-100/60",
};

const Item = ({
	children,
	onSelect,
	variant = "default",
	className,
	disabled,
	_index,
	...props
}: DropdownMenuItemProps & { _index?: number }) => {
	const { setIsOpen, activeIndex, getItemProps, listRef } =
		useDropdownMenuContext();

	const index = _index ?? 0;

	return (
		<button
			ref={(node) => {
				listRef.current[index] = node;
			}}
			role="menuitem"
			tabIndex={activeIndex === index ? 0 : -1}
			className={clsx(
				"flex w-full items-center gap-8 px-12 py-8 text-left text-sm transition-colors duration-100 outline-none",
				disabled
					? "cursor-not-allowed text-grayscale-400"
					: itemVariants[variant],
				className,
			)}
			disabled={disabled}
			{...getItemProps({
				onClick() {
					if (!disabled) {
						onSelect?.();
						setIsOpen(false);
					}
				},
			})}
			{...props}
		>
			{children}
		</button>
	);
};

// ─── Separator ───────────────────────────────────────────────────────

const Separator = ({ className, ...props }: DropdownMenuSeparatorProps) => (
	<div
		role="separator"
		className={clsx("my-4 h-px bg-grayscale-200", className)}
		{...props}
	/>
);

// ─── Export ──────────────────────────────────────────────────────────

const DropdownMenu = Object.assign(DropdownMenuRoot, {
	Trigger,
	Content,
	Item,
	Separator,
});

export default DropdownMenu;
